//Deep links into a Page Builder page, e.g. /bsm2026#tue-0930 or /bsm2026#session-2952.
//
//Two kinds of anchor:
//  - an element's optional "anchor" field (element.details.anchor), set in the builder;
//    ElementViewer renders it as the id of the element's wrapper;
//  - every session tile gets `session-<conference id>` automatically (Conferences.js),
//    so a single session can be linked without any setup. Conference ids survive page
//    duplicates and url swaps; page ids do not, which is why links never name a tab.
//
//Opt-in by construction: no anchor = no id, and a url without a #hash runs none of this.
//Nothing is stored anywhere new: the anchor lives in the page JSON the builder already saves.

//Clearance for the fixed site navbar when scrolling to an anchor.
export const ANCHOR_SCROLL_MARGIN_PX = 96;
//Tiles load in their own request after the ruleset check (up to ~15 s in the worst case),
//so a tile anchor can appear well after the page itself.
const REVEAL_TIMEOUT_MS = 20000;
const POLL_MS = 250;
//Content above the target keeps loading after the first scroll and pushes it down.
const RESETTLE_AFTER_MS = [1200, 3000];

export const TAB_REVEAL_EVENT = 'pb:reveal-tab';

//"Tue 09:30" -> "tue-09-30". Letters, digits, - and _ only, lower case, and it must start
//with a letter so it is a valid, selector-safe id. Anything else yields undefined (no id).
export const toAnchorId = (raw) => {
  if (raw === undefined || raw === null) return undefined;
  const id = String(raw)
    .trim()
    .toLowerCase()
    .replace(/^#/, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
  return /^[a-z][a-z0-9_-]*$/.test(id) ? id : undefined;
};

export const sessionAnchorId = (conferenceId) =>
  conferenceId === undefined || conferenceId === null || conferenceId === ''
    ? undefined
    : toAnchorId(`session-${conferenceId}`);

export const hashAnchorId = (hash) => {
  let value = hash === undefined ? window.location.hash : hash;
  if (!value) return undefined;
  try {
    value = decodeURIComponent(value);
  } catch (e) {
    //a malformed escape is not an anchor we could have written
    return undefined;
  }
  return toAnchorId(value.replace(/^#/, ''));
};

const isShown = (el) => el.getClientRects().length > 0;

const scrollTo = (el) => {
  el.style.scrollMarginTop = `${ANCHOR_SCROLL_MARGIN_PX}px`;
  el.scrollIntoView({ block: 'start', behavior: 'smooth' });
};

//Finds the element for `id`, opens the day tab that holds it if needed, and scrolls to it.
//Returns a cancel function. Stops quietly if the id never appears or the visitor starts
//scrolling themselves (we never fight the visitor for the scroll position).
export const revealAnchor = (id) => {
  if (!id) return () => {};
  let cancelled = false;
  let userMoved = false;
  const timers = [];
  const markMoved = () => {
    userMoved = true;
  };
  const moveEvents = ['wheel', 'touchmove', 'keydown', 'mousedown'];
  moveEvents.forEach((e) => window.addEventListener(e, markMoved, { passive: true }));
  const cleanup = () => {
    cancelled = true;
    timers.forEach(clearTimeout);
    moveEvents.forEach((e) => window.removeEventListener(e, markMoved));
  };

  const startedAt = Date.now();
  let askedForTab = false;
  const poll = () => {
    if (cancelled) return;
    if (Date.now() - startedAt > REVEAL_TIMEOUT_MS) return cleanup();
    const el = document.getElementById(id);
    if (el && !isShown(el) && !askedForTab) {
      //it sits in a day tab that is not the open one: ask that tab to open
      const pane = el.closest('[data-pb-tab-key]');
      if (pane) {
        askedForTab = true;
        window.dispatchEvent(
          new CustomEvent(TAB_REVEAL_EVENT, {
            detail: { key: pane.getAttribute('data-pb-tab-key') }
          })
        );
      }
    }
    if (!el || !isShown(el)) {
      timers.push(setTimeout(poll, POLL_MS));
      return;
    }
    scrollTo(el);
    RESETTLE_AFTER_MS.forEach((ms) =>
      timers.push(
        setTimeout(() => {
          if (!cancelled && !userMoved && isShown(el)) scrollTo(el);
        }, ms)
      )
    );
    timers.push(
      setTimeout(cleanup, RESETTLE_AFTER_MS[RESETTLE_AFTER_MS.length - 1] + 100)
    );
  };
  poll();
  return cleanup;
};
